/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { Monitor, SearchX } from "lucide-react";

import {
  LoadingBlock,
  Spinner,
  T9,
} from "@src/components/modules/WizardModule/WizardKit";
import { ThemeProps } from "@src/components/Theme";
import Checkbox from "@src/components/ui/Checkbox";
import IconTile from "@src/components/ui/IconTile";
import InfoIcon from "@src/components/ui/InfoIcon";
import EmptyState from "@src/components/ui/Lists/EmptyState";
import { Pill } from "@src/components/ui/Lists/ListTable";
import ArrowPagination from "@src/components/ui/Pagination/ArrowPagination";
import ReloadButton from "@src/components/ui/ReloadButton";
import SearchInput from "@src/components/ui/SearchInput";

import type { Instance as InstanceType } from "@src/@types/Instance";

const mbToGbString = (mb: number) =>
  mb >= 1024 ? `${(mb / 1024).toFixed(2)} GB` : `${mb} MB`;

const Wrapper = styled.div<any>`
  width: 100%;
  display: flex;
  flex-direction: column;
`;
const InstancesWrapper = styled.div<any>`
  flex-grow: 1;
  overflow: auto;
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
`;
const InstanceContent = styled.div<any>`
  display: flex;
  align-items: center;
  flex-grow: 1;
  min-width: 0;
  padding: 10px 16px 10px 12px;
`;
const CheckboxStyled = styled(Checkbox)`
  margin-left: 16px;
  flex-shrink: 0;
`;
const Instance = styled.div<any>`
  display: flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  border-top: 1px solid ${T9.slate200};
  background: ${props => (props.selected ? T9.brand50 : "var(--t9-bg-surface)")};
  transition: background ${ThemeProps.animations.swift};

  &:first-child {
    border-top: none;
  }
  &:hover {
    background: ${props => (props.selected ? T9.brand50 : T9.slate50)};
  }
`;
const OS_LABELS: { [os: string]: string } = { linux: "Linux", windows: "Windows" };
/** A VM: a Monitor icon tile (the OS, when known, is a small pill next to the name). */
export const InstanceImage = ({ os, size = 36 }: { os?: string; size?: number }) => (
  <IconTile
    icon={Monitor}
    size={size}
    tone="brand"
    title={os && OS_LABELS[os] ? `${OS_LABELS[os]} VM` : undefined}
  />
);
export const OsPill = ({ os }: { os?: string | null }) =>
  os && OS_LABELS[os] ? <Pill tone="neutral">{OS_LABELS[os]}</Pill> : null;
const POWER: {
  [state: string]: { label: string; tone: "success" | "neutral" | "warning" };
} = {
  on: { label: "On", tone: "success" }, // power state wording: On / Off (UI spec 8.4)
  off: { label: "Off", tone: "neutral" },
  suspended: { label: "Suspended", tone: "warning" },
};
/** The VM's power state, where the source cloud reports it. */
export const PowerPill = ({ state }: { state?: string | null }) =>
  state && POWER[state] ? (
    <Pill
      tone={POWER[state].tone}
      title={`The VM is ${POWER[state].label.toLowerCase()} now`}
    >
      {POWER[state].label}
    </Pill>
  ) : null;
const Label = styled.div<any>`
  flex-grow: 1;
  min-width: 0;
  margin: 0 16px 0 12px;
  display: flex;
  flex-direction: column;
`;
const LabelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${T9.slate900};
`;
const LabelSubtitle = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${T9.slate500};
  overflow-wrap: anywhere;
`;
const Details = styled.div<any>`
  font-size: 13px;
  color: ${T9.slate500};
  min-width: 160px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;
const FiltersWrapper = styled.div<any>`
  min-height: 34px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const SearchInputInfo = styled.div<any>`
  display: flex;
  align-items: center;
`;
const FilterInfo = styled.div<any>`
  display: flex;
  align-items: center;
  font-size: 13px;
  color: ${T9.slate500};
`;
const SelectionInfo = styled.div<any>`
  strong {
    color: ${T9.slate900};
    font-weight: 600;
  }
`;
const FilterSeparator = styled.div<any>`
  width: 1px;
  height: 16px;
  background: ${T9.slate200};
  margin: 0 12px;
`;
const Reloading = styled.div<any>`
  display: flex;
  justify-content: center;
  margin: 48px auto 0 auto;
  flex-grow: 1;
`;
const SOURCE_OPTIONS_HINT =
  "Some clouds need options such as a location or resource container before they can list VMs. Check the options on the previous step.";

type Props = {
  instances: InstanceType[];
  selectedInstances?: InstanceType[] | null;
  currentPage: number;
  instancesPerPage: number;
  loading: boolean;
  chunksLoading: boolean;
  searching: boolean;
  searchNotFound: boolean;
  reloading: boolean;
  hasSourceOptions: boolean;
  searchText?: string;
  onSearchInputChange: (value: string) => void;
  onReloadClick: () => void;
  onInstanceClick: (instance: InstanceType) => void;
  onPageClick: (page: number) => void;
};
type State = {
  searchText: string;
};

@observer
class WizardInstances extends React.Component<Props, State> {
  state = {
    searchText: "",
  };

  timeout!: number;

  isCheckboxMouseDown = false;

  componentWillUnmount() {
    this.props.onSearchInputChange("");
  }

  handleSeachInputChange(searchText: string) {
    clearTimeout(this.timeout);
    this.setState({ searchText });
    this.timeout = window.setTimeout(() => {
      this.props.onSearchInputChange(searchText);
    }, 500);
  }

  handlePreviousPageClick() {
    this.props.onPageClick(this.props.currentPage - 1);
  }

  handleNextPageClick() {
    this.props.onPageClick(this.props.currentPage + 1);
  }

  areNoInstances() {
    return (
      !this.props.loading &&
      !this.props.searchNotFound &&
      !this.props.reloading &&
      this.props.instances.length === 0 &&
      !this.props.searching
    );
  }

  renderNoInstances() {
    if (!this.areNoInstances()) {
      return null;
    }

    return (
      <EmptyState
        icon={Monitor}
        title="No VMs in this endpoint"
        message={
          this.props.hasSourceOptions
            ? SOURCE_OPTIONS_HINT
            : "Search again, or choose another endpoint."
        }
        secondaryLabel="Search again"
        onSecondary={() => {
          this.props.onReloadClick();
        }}
      />
    );
  }

  renderSearchNotFound() {
    if (!this.props.searchNotFound) {
      return null;
    }

    return (
      <EmptyState
        icon={SearchX}
        tone="neutral"
        title="No VMs match your search"
        message={
          this.props.hasSourceOptions
            ? SOURCE_OPTIONS_HINT
            : "Try another name or ID."
        }
        secondaryLabel="Try again"
        onSecondary={() => {
          this.props.onReloadClick();
        }}
      />
    );
  }

  renderReloading() {
    if (!this.props.reloading) {
      return null;
    }

    return (
      <Reloading>
        <Spinner size={32} />
      </Reloading>
    );
  }

  renderLoading() {
    if (!this.props.loading) {
      return null;
    }

    return <LoadingBlock label="Loading VMs…" />;
  }

  renderInstances() {
    if (
      this.props.loading ||
      this.props.searchNotFound ||
      this.props.reloading ||
      this.areNoInstances()
    ) {
      return null;
    }
    const startIdx = (this.props.currentPage - 1) * this.props.instancesPerPage;
    const endIdx = startIdx + (this.props.instancesPerPage - 1);
    const filteredInstances = this.props.instances.filter(
      (_, idx) => idx >= startIdx && idx <= endIdx,
    );

    return (
      <InstancesWrapper>
        {filteredInstances.map(instance => {
          const selected = Boolean(
            this.props.selectedInstances &&
              this.props.selectedInstances.find(i => i.id === instance.id),
          );
          const flavorName = instance.flavor_name
            ? ` · ${instance.flavor_name}`
            : "";
          const instanceId = instance.instance_name || instance.id;
          return (
            <Instance
              key={instance.id}
              onMouseDown={() => {
                if (!this.isCheckboxMouseDown)
                  this.props.onInstanceClick(instance);
              }}
              selected={selected}
            >
              <CheckboxStyled
                checked={selected}
                onChange={() => {
                  this.props.onInstanceClick(instance);
                }}
                onMouseDown={() => {
                  this.isCheckboxMouseDown = true;
                }}
                onMouseUp={() => {
                  this.isCheckboxMouseDown = false;
                }}
              />
              <InstanceContent>
                <InstanceImage os={instance.os_type} />
                <Label>
                  <LabelTitle>
                    <span>{instance.name}</span>
                    <OsPill os={instance.os_type} />
                    <PowerPill state={instance.power_state} />
                  </LabelTitle>
                  {instanceId !== instance.name ? (
                    <LabelSubtitle>{instanceId}</LabelSubtitle>
                  ) : null}
                </Label>
                <Details>
                  {`${instance.num_cpu} vCPU · ${mbToGbString(
                    instance.memory_mb,
                  )} RAM${flavorName}`}
                </Details>
              </InstanceContent>
            </Instance>
          );
        })}
      </InstancesWrapper>
    );
  }

  renderFilters() {
    if (this.props.loading || this.areNoInstances()) {
      return null;
    }

    const count = this.props.selectedInstances
      ? this.props.selectedInstances.length
      : 0;
    const plural = count === 1 ? "" : "s";

    return (
      <FiltersWrapper>
        <SearchInputInfo>
          <SearchInput
            alwaysOpen
            onChange={searchText => {
              this.handleSeachInputChange(searchText);
            }}
            value={this.state.searchText}
            loading={this.props.searching}
            placeholder="Search VMs"
          />
          {this.props.hasSourceOptions ? (
            <InfoIcon
              text={SOURCE_OPTIONS_HINT}
              marginBottom={0}
              marginLeft={8}
              filled
            />
          ) : null}
        </SearchInputInfo>
        <FilterInfo>
          <SelectionInfo>
            <strong>{count}</strong> VM{plural} selected
          </SelectionInfo>
          <FilterSeparator>|</FilterSeparator>
          <ReloadButton
            onClick={() => {
              this.props.onReloadClick();
            }}
          />
        </FilterInfo>
      </FiltersWrapper>
    );
  }

  renderPagination() {
    if (
      this.props.loading ||
      this.props.searchNotFound ||
      this.props.reloading ||
      this.areNoInstances()
    ) {
      return null;
    }

    const hasNextPage =
      this.props.currentPage * this.props.instancesPerPage <
      this.props.instances.length;
    const areAllDisabled = this.props.searching;
    const isPreviousDisabled = this.props.currentPage === 1 || areAllDisabled;
    const isNextDisabled = !hasNextPage || areAllDisabled;

    return (
      <ArrowPagination
        style={{ margin: "32px 0 16px 0" }}
        previousDisabled={isPreviousDisabled}
        onPreviousClick={() => {
          this.handlePreviousPageClick();
        }}
        currentPage={this.props.currentPage}
        totalPages={Math.ceil(
          this.props.instances.length / this.props.instancesPerPage,
        )}
        loading={this.props.chunksLoading}
        nextDisabled={isNextDisabled}
        onNextClick={() => {
          this.handleNextPageClick();
        }}
      />
    );
  }

  render() {
    return (
      <Wrapper>
        {this.renderFilters()}
        {this.renderLoading()}
        {this.renderReloading()}
        {this.renderSearchNotFound()}
        {this.renderInstances()}
        {this.renderPagination()}
        {this.renderNoInstances()}
      </Wrapper>
    );
  }
}

export default WizardInstances;
